import { useEffect, useState } from "react";
import { fetchCalendarEvents, fetchInboxCounts } from "../services/graph";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";

/**
 * The mailbox and calendar figures the Outlook boot greeting quotes (spec
 * ST-02, Figure 3.1: "I've analysed your mailbox and calendar (25 emails,
 * 4 events today)").
 *
 * `unavailable` is distinct from `pending` on purpose: a greeting that quietly
 * drops the briefing sentence forever looks identical to one still loading, and
 * would leave the both-connected state showing copy ST-02 does not document.
 * A failed read is reported, not hidden.
 */
export type MailboxSnapshot =
  | { readonly status: "pending" }
  | { readonly status: "ready"; readonly unreadEmails: number; readonly eventsToday: number }
  | { readonly status: "unavailable" };

const PENDING: MailboxSnapshot = { status: "pending" };
const UNAVAILABLE: MailboxSnapshot = { status: "unavailable" };

/**
 * Loads the two counts behind the greeting.
 *
 * `enabled` is the greeting's own question — "does this state quote counts?" —
 * and is false off Outlook, where they have no meaning. It is deliberately
 * separate from the token: `m365Connected` is a Studio config flag that
 * defaults to true, so "we should have counts but hold no Graph token" is
 * reachable and must report `unavailable`, not the null that means
 * "not applicable here" and silently shortens the greeting forever.
 */
export function useMailboxSnapshot(
  token: string | null,
  enabled: boolean,
): MailboxSnapshot | null {
  const { refreshGraphToken } = useAuthState();
  const [snapshot, setSnapshot] = useState<MailboxSnapshot | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSnapshot(null);
      return;
    }
    if (!token) {
      setSnapshot(UNAVAILABLE);
      return;
    }

    let cancelled = false;
    setSnapshot(PENDING);

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    Promise.all([
      fetchInboxCounts(token, refreshGraphToken),
      fetchCalendarEvents(token, start.toISOString(), end.toISOString(), refreshGraphToken),
    ])
      .then(([counts, events]) => {
        if (cancelled) return;
        setSnapshot({
          status: "ready",
          unreadEmails: counts.unread,
          eventsToday: events.length,
        });
        logConnectorSuccess("email", "greeting-snapshot");
      })
      .catch((e: unknown) => {
        // A failed count must not blank the greeting — it says so instead.
        if (cancelled) return;
        setSnapshot(UNAVAILABLE);
        logConnectorFailure("email", e, "greeting-snapshot");
      });

    return () => {
      cancelled = true;
    };
  }, [token, enabled, refreshGraphToken]);

  return snapshot;
}
